/* ============================================================================
   particles.ts — Systeme de particules d'etoiles magiques pour le mapping
   "peace sign" (Y/V sign) du hand tracking.

   Le state est mute en place (pas de re-creation d'objet par frame) et le
   rendu se fait via canvas 2D natif. Aucune dependance externe.

   Usage typique (dans une rAF loop) :
     const ps = new ParticleSystem();
     // ... chaque frame :
     if (peaceDetected) ps.emitFrom(tipX, tipY);
     else ps.stop();
     ps.update(dt);
     ps.render(ctx);
   ============================================================================ */

export interface StarParticle {
  x: number; // position canvas (px)
  y: number;
  vx: number; // velocity px/sec
  vy: number;
  glyph: string; // unicode star : ✦ ✧ ⋆ ✶ ✷ ✩ ❉ ✨
  size: number; // font-size px
  age: number; // ms depuis emission
  lifetime: number; // ms total
  rotation: number; // deg
  rotVel: number; // deg/sec
  hueShift: number; // pour variation visuelle (0..360)
}

/** Limite dure pour eviter les fuites memoire en cas de hold prolonge. */
const MAX_PARTICLES = 100;

/** Mode de particule : 'star' (peace sign) ou 'heart' (finger heart). */
export type ParticleMode = 'star' | 'heart';

const STAR_GLYPHS = ['✦', '✧', '⋆', '✶', '✷', '✩', '❉', '✨'];
const HEART_GLYPHS = ['♡', '❥', '❣', '♥', '✿', '♥︎'];

export class ParticleSystem {
  particles: StarParticle[] = [];
  emitting = false;
  emitRate = 18; // particules par seconde quand emitting
  emitOriginX = 0;
  emitOriginY = 0;
  emitJitter = 60; // px jitter autour origin
  mode: ParticleMode = 'star';
  private accum = 0;
  /** Track la transition off→on pour declencher un burst initial. */
  private wasEmitting = false;

  emitFrom(x: number, y: number, mode: ParticleMode = 'star') {
    this.emitOriginX = x;
    this.emitOriginY = y;
    // Detection rising edge : transition off→on OU changement de mode →
    // burst initial (gros pulse de N particules au lieu d'attendre l'emit
    // graduel). Donne un effet "magique" plus immediat.
    const modeChanged = this.mode !== mode;
    if (!this.wasEmitting || modeChanged) {
      this.spawnBurst(mode);
    }
    this.mode = mode;
    this.emitting = true;
    this.wasEmitting = true;
    // Boost emit rate en mode heart (plus dense pour effet plus visible)
    this.emitRate = mode === 'heart' ? 28 : 18;
  }

  /**
   * Burst initial : spawn directement N particules au moment de la detection.
   * Donne un effet "pop" immediat au lieu d'attendre l'accumulation graduelle.
   */
  private spawnBurst(mode: ParticleMode) {
    const prevMode = this.mode;
    this.mode = mode;
    const burstSize = mode === 'heart' ? 8 : 5;
    for (let i = 0; i < burstSize; i++) {
      if (this.particles.length >= MAX_PARTICLES) break;
      this.particles.push(this.spawnOne());
    }
    this.mode = prevMode;
  }

  stop() {
    this.emitting = false;
    this.wasEmitting = false;
  }

  update(dt: number) {
    // 1. Emet de nouvelles particules si emitting
    if (this.emitting) {
      this.accum += dt;
      const interval = 1 / this.emitRate;
      while (this.accum >= interval) {
        this.accum -= interval;
        // Cap dur : skip emit si on est deja a la limite
        if (this.particles.length >= MAX_PARTICLES) break;
        this.particles.push(this.spawnOne());
      }
    } else {
      // Reset accum quand on n'emet pas pour eviter un burst au prochain start
      this.accum = 0;
    }
    // 2. Update les existantes
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.age += dt * 1000;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rotation += p.rotVel * dt;
      // Legere gravite inverse (les etoiles montent)
      p.vy -= 30 * dt;
      // Friction
      p.vx *= 1 - 0.4 * dt;
      if (p.age >= p.lifetime) {
        this.particles.splice(i, 1);
      }
    }
  }

  private spawnOne(): StarParticle {
    const angle = Math.random() * Math.PI * 2;
    const isHeart = this.mode === 'heart';
    // Hearts : plus lents, moins agressifs, drift latéral plus marqué
    const speed = isHeart ? 30 + Math.random() * 40 : 60 + Math.random() * 80;
    const glyphs = isHeart ? HEART_GLYPHS : STAR_GLYPHS;
    const upBias = isHeart ? -50 : -80;
    return {
      x: this.emitOriginX + (Math.random() - 0.5) * this.emitJitter,
      y: this.emitOriginY + (Math.random() - 0.5) * this.emitJitter,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed + upBias,
      glyph: glyphs[Math.floor(Math.random() * glyphs.length)]!,
      size: isHeart ? 14 + Math.random() * 18 : 12 + Math.random() * 16,
      age: 0,
      lifetime: isHeart ? 1800 + Math.random() * 1000 : 1200 + Math.random() * 800,
      rotation: isHeart ? (Math.random() - 0.5) * 30 : Math.random() * 360,
      rotVel: isHeart ? (Math.random() - 0.5) * 60 : (Math.random() - 0.5) * 240,
      hueShift: Math.random() * 360,
    };
  }

  /** Rendu canvas 2D — palette grayscale strict (ink + alpha). */
  render(ctx: CanvasRenderingContext2D) {
    const now = performance.now();
    for (const p of this.particles) {
      const lifeFrac = p.age / p.lifetime;
      const alpha = Math.max(0, 1 - lifeFrac);
      // Scale : croit avec l'age + pulse "battement de coeur" pour les hearts
      const isHeart = HEART_GLYPHS.includes(p.glyph);
      let scale = 1 + lifeFrac * 0.5;
      if (isHeart) {
        // Pulse 2 Hz superpose, amplitude ±15% (vrai battement de coeur)
        scale *= 1 + 0.15 * Math.sin((now / 1000 + p.hueShift) * 2 * Math.PI * 2);
      }
      // Fade-in court pour les nouvelles particules (premiers 100ms)
      const fadeIn = Math.min(1, p.age / 100);
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.scale(scale, scale);
      ctx.font = `${p.size}px serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = `rgba(13, 13, 13, ${alpha * fadeIn})`;
      ctx.fillText(p.glyph, 0, 0);
      ctx.restore();
    }
  }
}
